import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField } from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  useErrorClearing,
  useUniqueNameValidation,
} from 'hooks/GoalsAndSubgoalsForm';
import { formTexts } from 'i18n';
import { GoalField } from 'components/GoalsAndSubgoalsForm/interfaces';
import {
  ChevronButton,
  ContentArea,
  DescriptionFieldGroup,
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  Header,
  HeaderInner,
  HeaderTitle,
  RemoveGoalButton,
  SectionRoot,
  SubgoalsBranch,
  SubgoalsBranchInner,
  SubgoalsHeader,
  SubgoalsList,
  descriptionFieldSx,
} from 'pages/Scorecard/EditScorecard/GoalEditor.styled';
import CommonGoalSubgoalEditor from './CommonGoalSubgoalEditor.component';
import useCommonGoalEditGating from './useCommonGoalEditGating';

interface CommonGoalCuSatEditorProps {
  goalIndex: number;
  goalsCount: number;
  onRemove?: () => void;
}

export default function CommonGoalCuSatEditor({
  goalIndex,
  goalsCount,
  onRemove,
}: CommonGoalCuSatEditorProps) {
  const { control, watch } = useFormContext<GoalField>();
  const { formatMessage } = useIntl();
  const goal = watch(`goals.${goalIndex}`);
  const [expanded, setExpanded] = useState(true);
  const nameValidationRules = useUniqueNameValidation('goals', goalIndex);
  useErrorClearing(goalIndex);
  const { canDeleteGoal } = useCommonGoalEditGating(goalIndex, goalsCount);

  const subgoalsPath = `goals.${goalIndex}.subgoals` as const;
  const { fields } = useFieldArray({
    control,
    name: subgoalsPath,
  });

  const goalNamePlaceholder = formatMessage({
    id: 'pages.scorecards.edit.goalNamePlaceholder',
    defaultMessage: 'Goal name',
  });
  const descriptionPlaceholder = formatMessage({
    id: 'pages.scorecards.edit.descriptionPlaceholder',
    defaultMessage: 'Add a description for this goal',
  });

  return (
    <SectionRoot>
      <Header>
        <HeaderInner>
          <ChevronButton
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'collapse goal' : 'expand goal'}
          >
            {expanded ? (
              <ExpandLess sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16 }} />
            )}
          </ChevronButton>
          <HeaderTitle>{goal?.name || `Goal ${goalIndex + 1}`}</HeaderTitle>
          {onRemove && canDeleteGoal && (
            <RemoveGoalButton
              onClick={onRemove}
              size="small"
              aria-label="remove goal"
            >
              <DeleteIcon fontSize="small" />
            </RemoveGoalButton>
          )}
        </HeaderInner>
      </Header>
      {expanded && (
        <ContentArea>
          <FieldsGrid>
            <FieldGroup className="name">
              <FieldLabel htmlFor={`cg-goal-${goalIndex}-name`}>
                <FormattedMessage
                  id="pages.scorecards.edit.goalName"
                  defaultMessage="Goal Name"
                />
              </FieldLabel>
              <Controller
                name={`goals.${goalIndex}.name`}
                control={control}
                rules={{
                  required: formatMessage(formTexts.errorRequired),
                  maxLength: {
                    value: 256,
                    message: formatMessage(formTexts.errorMaxLength, {
                      fieldName: '',
                      maxLength: 256,
                    }),
                  },
                  validate: nameValidationRules,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    id={`cg-goal-${goalIndex}-name`}
                    fullWidth
                    size="small"
                    placeholder={goalNamePlaceholder}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 256 }}
                    disabled
                  />
                )}
              />
            </FieldGroup>
          </FieldsGrid>
          <DescriptionFieldGroup>
            <FieldLabel htmlFor={`cg-goal-${goalIndex}-description`}>
              <FormattedMessage
                id="pages.scorecards.edit.description"
                defaultMessage="Description"
              />
            </FieldLabel>
            <Controller
              name={`goals.${goalIndex}.description`}
              control={control}
              rules={{
                maxLength: {
                  value: 3000,
                  message: formatMessage(formTexts.errorMaxLength, {
                    fieldName: '',
                    maxLength: 3000,
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  id={`cg-goal-${goalIndex}-description`}
                  fullWidth
                  size="small"
                  multiline
                  placeholder={descriptionPlaceholder}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  inputProps={{ maxLength: 3000 }}
                  disabled
                  sx={descriptionFieldSx}
                />
              )}
            />
          </DescriptionFieldGroup>
          <SubgoalsBranch>
            <SubgoalsBranchInner>
              <SubgoalsHeader>
                <FormattedMessage
                  id="pages.scorecards.edit.subgoals"
                  defaultMessage="Subgoals"
                />
              </SubgoalsHeader>
              <SubgoalsList>
                {fields.map((field, subgoalIndex) => (
                  <CommonGoalSubgoalEditor
                    key={field.id}
                    goalIndex={goalIndex}
                    subgoalIndex={subgoalIndex}
                    onRemove={() => undefined}
                  />
                ))}
              </SubgoalsList>
            </SubgoalsBranchInner>
          </SubgoalsBranch>
        </ContentArea>
      )}
    </SectionRoot>
  );
}
